import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/icons';
import { AsyncPanel, Panel } from '@/components/platform/AsyncPanel';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { Checkbox } from '@/components/ui/Checkbox';
import { platformApi } from '@/api/platform';
import { InstitutionName, InstitutionPicker } from '@/components/platform/InstitutionPicker';
import { useAsyncData } from '@/hooks';
import {
  INCIDENT_SEVERITIES,
  MAINTENANCE_MODES,
  OPEN_INCIDENT_STATUSES,
  operationalStateIsReported,
  type IncidentSeverity,
  type IncidentStatus,
  type MaintenanceMode,
  type OperationalState,
  type PlatformHealthSnapshot,
  type PlatformIncident,
  type ProductMaintenanceWindow,
} from '@/types/platform';
import { formatCount, formatDateTime, formatRelative } from '@/utils/format';
import styles from './platform-pages.module.css';

/**
 * PA-13 — is the platform working, and if not, which part.
 *
 * THREE RESOURCES, NOT ONE SNAPSHOT. This screen used to make a single request to
 * `/platform/operations`, which is not a route and never has been, so the whole
 * page reported itself as undeployed while health, incidents and maintenance were
 * all live. They are separate now because they fail separately: a health query
 * that times out must not take the incident list — the thing an operator is most
 * likely to be here for — down with it.
 *
 * NOT_REPORTING AND UNKNOWN ARE NOT SHADES OF GREEN. Core will not claim a signal
 * is healthy because nothing has reported to it, and this screen must not undo
 * that. Both render in their own tone, both carry the `wouldRequire` sentence
 * saying what is missing, and the `honesty` block is shown in full rather than
 * summarised — a board with holes in it has to look like a board with holes in it.
 *
 * There is no auto-refresh. A page that reloads on a timer is a page whose numbers
 * are trusted without anyone checking when they were taken, and the "as of" line
 * is the one thing an operator needs when they are deciding whether an incident is
 * still happening.
 */

const STATE_TONES: Record<OperationalState, string> = {
  HEALTHY: 'okTag',
  DEGRADED: 'warnTag',
  UNHEALTHY: 'dangerTag',
  NOT_REPORTING: 'warningTag',
  UNKNOWN: 'mutedTag',
};

const SEVERITY_TONES: Record<IncidentSeverity, string> = {
  CRITICAL: 'criticalTag',
  MAJOR: 'dangerTag',
  MINOR: 'warnTag',
};

const MIN_TEXT = 10;

type IncidentDialog =
  | { kind: 'RAISE' }
  | { kind: 'UPDATE'; incident: PlatformIncident }
  | { kind: 'RESOLVE'; incident: PlatformIncident };

export function OperationsPage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const [openOnly, setOpenOnly] = useState(true);
  const [incidentDialog, setIncidentDialog] = useState<IncidentDialog | null>(null);
  const [schedulingMaintenance, setSchedulingMaintenance] = useState(false);
  const [maintenanceError, setMaintenanceError] = useState<string | null>(null);

  const healthFetcher = useCallback(() => platformApi.operations.health(), []);
  const { state: health, reload: reloadHealth } = useAsyncData<PlatformHealthSnapshot>(
    'operations-health',
    healthFetcher,
  );

  const incidentsFetcher = useCallback(() => platformApi.operations.incidents({ openOnly }), [openOnly]);
  const { state: incidents, reload: reloadIncidents } = useAsyncData<PlatformIncident[]>(
    `operations-incidents:${openOnly}`,
    incidentsFetcher,
  );

  const maintenanceFetcher = useCallback(() => platformApi.operations.maintenance(), []);
  const { state: maintenance, reload: reloadMaintenance } = useAsyncData<ProductMaintenanceWindow[]>(
    'operations-maintenance',
    maintenanceFetcher,
  );

  const reloadAll = () => {
    reloadHealth();
    reloadIncidents();
    reloadMaintenance();
  };

  const cancelWindow = async (window: ProductMaintenanceWindow) => {
    setMaintenanceError(null);
    try {
      await platformApi.operations.cancelMaintenance(window.id);
      reloadMaintenance();
      reloadHealth();
    } catch (cause) {
      setMaintenanceError((cause as Error).message);
    }
  };

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>{t('platform.operations.title')}</h1>
          <p className={styles.pageLead}>{t('platform.operations.lead')}</p>
        </div>
        <button type="button" className={styles.secondaryBtn} onClick={reloadAll}>
          <Icon name="refresh" size={15} />
          {t('platform.dashboard.refresh')}
        </button>
      </header>

      <Panel title={t('platform.operations.healthTitle')} icon="pulse">
        <AsyncPanel state={health} onRetry={reloadHealth}>
          {(snapshot) => (
            <>
              <div className={styles.caseHeader}>
                <div>
                  <p className={styles.rowTitle}>
                    <span className={styles[STATE_TONES[snapshot.overall]] ?? styles.mutedTag}>
                      {t(`platform.operations.state.${snapshot.overall}`, { defaultValue: snapshot.overall })}
                    </span>
                  </p>
                  <p className={styles.rowMetaLine}>
                    {t('platform.operations.incidentSummary', {
                      incidents: formatCount(snapshot.openIncidents, locale),
                      windows: formatCount(snapshot.activeMaintenanceWindows, locale),
                    })}
                  </p>
                </div>
                {snapshot.generatedAt && (
                  <p className={styles.generatedAt}>
                    {t('platform.dashboard.generatedAt', {
                      time: formatDateTime(snapshot.generatedAt, locale),
                    })}
                  </p>
                )}
              </div>

              {/*
                The honesty block, verbatim. Summarising it — "3 signals not
                measured" — would let the reader skip the part that says which
                three, and the whole point is that they cannot.
              */}
              <div className={styles.warningNotice} role="note">
                <Icon name="info" size={16} aria-hidden="true" />
                <div>
                  <p className={styles.warningTitle}>{t('platform.operations.honestyTitle')}</p>
                  <p className={styles.warningBody}>{snapshot.honesty.statement}</p>
                  <dl className={styles.detailGrid}>
                    <dt>{t('platform.operations.measuredHere')}</dt>
                    <dd>{joinOrDash(snapshot.honesty.measuredHere)}</dd>
                    <dt>{t('platform.operations.notReportingLabel')}</dt>
                    <dd>{joinOrDash(snapshot.honesty.notReporting)}</dd>
                    <dt>{t('platform.operations.notMeasurable')}</dt>
                    <dd>{joinOrDash(snapshot.honesty.notMeasurable)}</dd>
                  </dl>
                </div>
              </div>

              {snapshot.signals.length === 0 ? (
                <p className={styles.sectionUnavailable} role="status">
                  {t('platform.operations.noSignals')}
                </p>
              ) : (
                <ul className={styles.rowList}>
                  {snapshot.signals.map((signal) => (
                    <li key={signal.key} className={styles.row}>
                      <div className={styles.rowMain}>
                        <p className={styles.rowTitle}>{signal.title}</p>
                        <p className={styles.rowMetaLine}>{signal.basis}</p>
                        {!operationalStateIsReported(signal.state) && signal.wouldRequire && (
                          <p className={styles.rowDetailError}>
                            {t('platform.operations.wouldRequire', { detail: signal.wouldRequire })}
                          </p>
                        )}
                        {Object.keys(signal.measurements).length > 0 && (
                          <p className={styles.rowDetail}>
                            {Object.entries(signal.measurements)
                              .map(([key, value]) => `${key}: ${value ?? '—'}`)
                              .join(' · ')}
                          </p>
                        )}
                      </div>
                      <div className={styles.rowAside}>
                        <span className={styles[STATE_TONES[signal.state]] ?? styles.mutedTag}>
                          {t(`platform.operations.state.${signal.state}`, { defaultValue: signal.state })}
                        </span>
                        <span className={styles.serviceMeta}>{formatRelative(signal.observedAt, locale)}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </AsyncPanel>
      </Panel>

      <Panel
        title={t('platform.operations.incidents')}
        icon="warning"
        emphasis="attention"
        actions={
          <>
            <Checkbox
              label={t('platform.operations.openOnly')}
              checked={openOnly}
              onChange={(event) => setOpenOnly(event.target.checked)}
            />
            <button
              type="button"
              className={styles.primaryBtnSm}
              onClick={() => setIncidentDialog({ kind: 'RAISE' })}
            >
              <Icon name="plus" size={15} />
              {t('platform.operations.raise')}
            </button>
          </>
        }
      >
        <AsyncPanel
          state={incidents}
          onRetry={reloadIncidents}
          isEmpty={(data) => data.length === 0}
          emptyTitle={t('platform.operations.noIncidents')}
          emptyBody={t('platform.operations.noIncidentsBody')}
        >
          {(data) => (
            <ul className={styles.rowList}>
              {data.map((incident) => (
                <li key={incident.id} className={styles.row}>
                  <div className={styles.rowMain}>
                    <p className={styles.rowTitle}>{incident.title}</p>
                    <p className={styles.rowMetaLine}>
                      <code className={styles.code}>{incident.reference}</code>
                      {' · '}
                      {formatRelative(incident.detectedAt, locale)}
                      {' · '}
                      {incident.platformWide
                        ? t('platform.operations.platformWide')
                        : t('platform.operations.institutionCount', {
                            n: incident.affectedInstitutionIds.length,
                          })}
                    </p>
                    <p className={styles.rowDetail}>{incident.summary}</p>
                    {incident.affectedProductCodes.length > 0 && (
                      <p className={styles.rowMetaLine}>
                        {incident.affectedProductCodes.map((code) => (
                          <span key={code} className={styles.codeTag}>
                            {code}
                          </span>
                        ))}
                      </p>
                    )}
                    {incident.resolution && (
                      <p className={styles.rowDetail}>
                        {t('platform.operations.resolutionNote', { note: incident.resolution })}
                      </p>
                    )}
                  </div>
                  <div className={styles.rowAside}>
                    <span className={styles[SEVERITY_TONES[incident.severity]] ?? styles.warnTag}>
                      {t(`platform.operations.severity.${incident.severity}`, {
                        defaultValue: incident.severity,
                      })}
                    </span>
                    <span className={styles.mutedTag}>
                      {t(`platform.operations.incidentStatus.${incident.status}`, {
                        defaultValue: incident.status,
                      })}
                    </span>
                    {OPEN_INCIDENT_STATUSES.includes(incident.status) && (
                      <div className={styles.rowAction}>
                        <button
                          type="button"
                          className={styles.linkBtn}
                          onClick={() => setIncidentDialog({ kind: 'UPDATE', incident })}
                        >
                          {t('platform.operations.update')}
                        </button>
                        <button
                          type="button"
                          className={styles.linkBtn}
                          onClick={() => setIncidentDialog({ kind: 'RESOLVE', incident })}
                        >
                          {t('platform.operations.resolve')}
                        </button>
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </AsyncPanel>
      </Panel>

      <Panel
        title={t('platform.operations.maintenance')}
        icon="clock"
        actions={
          <button type="button" className={styles.primaryBtnSm} onClick={() => setSchedulingMaintenance(true)}>
            <Icon name="plus" size={15} />
            {t('platform.operations.schedule')}
          </button>
        }
      >
        <p className={styles.dataNotice}>
          <Icon name="info" size={15} aria-hidden="true" />
          {t('platform.operations.maintenanceNotice')}
        </p>

        {maintenanceError && (
          <p className={styles.formError} role="alert">
            {maintenanceError}
          </p>
        )}

        <AsyncPanel
          state={maintenance}
          onRetry={reloadMaintenance}
          isEmpty={(data) => data.length === 0}
          emptyTitle={t('platform.operations.noWindows')}
          emptyBody={t('platform.operations.noWindowsBody')}
        >
          {(data) => (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <caption className={styles.tableCaption}>
                  {t('platform.operations.windowsCaption', { n: data.length })}
                </caption>
                <thead>
                  <tr>
                    <th scope="col">{t('platform.operations.colProduct')}</th>
                    <th scope="col">{t('platform.operations.colMode')}</th>
                    <th scope="col">{t('platform.operations.colScope')}</th>
                    <th scope="col">{t('platform.operations.colWindow')}</th>
                    <th scope="col">{t('platform.operations.colMessage')}</th>
                    <th scope="col">
                      <span className={styles.srOnlyText}>{t('platform.operations.colActions')}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((entry) => (
                    <tr key={entry.id}>
                      <th scope="row" className={styles.tableRowHeader}>
                        {entry.productCode}
                      </th>
                      <td>
                        <span className={entry.mode === 'UNAVAILABLE' ? styles.dangerTag : styles.warnTag}>
                          {t(`platform.operations.mode.${entry.mode}`, { defaultValue: entry.mode })}
                        </span>
                      </td>
                      <td className={styles.mutedCell}>
                        {entry.institutionId ?? t('platform.operations.everyInstitution')}
                      </td>
                      <td className={styles.mutedCell}>
                        {formatDateTime(entry.startsAt, locale)} → {formatDateTime(entry.endsAt, locale)}
                      </td>
                      <td>{entry.userMessage}</td>
                      <td>
                        <button type="button" className={styles.linkBtnDanger} onClick={() => cancelWindow(entry)}>
                          {t('platform.operations.endEarly')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </AsyncPanel>
      </Panel>

      <IncidentDialogView
        dialog={incidentDialog}
        onClose={() => setIncidentDialog(null)}
        onSaved={() => {
          setIncidentDialog(null);
          reloadIncidents();
          reloadHealth();
        }}
      />

      <ScheduleMaintenanceDialog
        open={schedulingMaintenance}
        onClose={() => setSchedulingMaintenance(false)}
        onSaved={() => {
          setSchedulingMaintenance(false);
          reloadMaintenance();
          reloadHealth();
        }}
      />
    </div>
  );
}

/** An empty list here means "none in this category", which is worth saying. */
function joinOrDash(values: readonly string[]): string {
  return values.length > 0 ? values.join(', ') : '—';
}

function IncidentDialogView({
  dialog,
  onClose,
  onSaved,
}: {
  dialog: IncidentDialog | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [severity, setSeverity] = useState<IncidentSeverity>('MAJOR');
  const [status, setStatus] = useState<Exclude<IncidentStatus, 'RESOLVED'>>('INVESTIGATING');
  const [platformWide, setPlatformWide] = useState(true);
  const [institutionIds, setInstitutionIds] = useState('');
  const [productCodes, setProductCodes] = useState('');
  const [resolution, setResolution] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reopening for a different incident must not inherit the last summary — that
  // is how the wrong description ends up on the wrong incident record.
  useEffect(() => {
    if (!dialog) return;
    setError(null);
    setResolution('');
    if (dialog.kind === 'RAISE') {
      setTitle('');
      setSummary('');
      setSeverity('MAJOR');
      setStatus('INVESTIGATING');
      setPlatformWide(true);
      setInstitutionIds('');
      setProductCodes('');
      return;
    }
    const { incident } = dialog;
    setTitle(incident.title);
    setSummary(incident.summary);
    setSeverity(incident.severity);
    setStatus(
      incident.status === 'RESOLVED' ? 'MITIGATED' : (incident.status as Exclude<IncidentStatus, 'RESOLVED'>),
    );
    setPlatformWide(incident.platformWide);
    setInstitutionIds(incident.affectedInstitutionIds.join(', '));
    setProductCodes(incident.affectedProductCodes.join(', '));
  }, [dialog]);

  if (!dialog) return null;

  const list = (value: string): string[] =>
    value
      .split(',')
      .map((entry) => entry.trim())
      .filter((entry) => entry.length > 0);

  const valid =
    dialog.kind === 'RESOLVE'
      ? resolution.trim().length >= MIN_TEXT
      : title.trim().length >= 3 && summary.trim().length >= MIN_TEXT;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!valid || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      if (dialog.kind === 'RAISE') {
        await platformApi.operations.raiseIncident({
          title: title.trim(),
          summary: summary.trim(),
          severity,
          platformWide,
          affectedInstitutionIds: platformWide ? undefined : list(institutionIds),
          affectedProductCodes: list(productCodes),
        });
      } else if (dialog.kind === 'UPDATE') {
        await platformApi.operations.updateIncident(dialog.incident.id, {
          status,
          severity,
          summary: summary.trim(),
          platformWide,
          affectedInstitutionIds: platformWide ? undefined : list(institutionIds),
          affectedProductCodes: list(productCodes),
        });
      } else {
        await platformApi.operations.resolveIncident(dialog.incident.id, resolution.trim());
      }
      onSaved();
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={t(`platform.operations.dialog.${dialog.kind}.title`)}
      description={t(`platform.operations.dialog.${dialog.kind}.lead`)}
      closeOnBackdropClick={false}
    >
      <form className={styles.dialogForm} onSubmit={submit} noValidate>
        {dialog.kind === 'RESOLVE' ? (
          <>
            {/*
              Resolving is not another status change. Mitigated means the symptom
              is gone; resolved means it will not come back for the same reason,
              and only the second needs somebody to say what was done.
            */}
            <div className={styles.warningNotice} role="note">
              <Icon name="info" size={16} aria-hidden="true" />
              <div>
                <p className={styles.warningTitle}>{t('platform.operations.resolveTitle')}</p>
                <p className={styles.warningBody}>{t('platform.operations.resolveBody')}</p>
              </div>
            </div>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>{t('platform.operations.resolutionLabel')}</span>
              <textarea
                className={styles.textarea}
                rows={4}
                value={resolution}
                required
                aria-invalid={resolution.length > 0 && resolution.trim().length < MIN_TEXT}
                onChange={(event) => setResolution(event.target.value)}
              />
              <span className={styles.fieldHint}>{t('platform.operations.resolutionHint')}</span>
            </label>
          </>
        ) : (
          <>
            {dialog.kind === 'RAISE' && (
              <label className={styles.field}>
                <span className={styles.fieldLabel}>{t('platform.operations.titleLabel')}</span>
                <input
                  className={styles.input}
                  value={title}
                  required
                  onChange={(event) => setTitle(event.target.value)}
                />
              </label>
            )}

            <label className={styles.field}>
              <span className={styles.fieldLabel}>{t('platform.operations.summaryLabel')}</span>
              <textarea
                className={styles.textarea}
                rows={3}
                value={summary}
                required
                aria-invalid={summary.length > 0 && summary.trim().length < MIN_TEXT}
                onChange={(event) => setSummary(event.target.value)}
              />
            </label>

            <div className={styles.dialogRow}>
              <label className={styles.field}>
                <span className={styles.fieldLabel}>{t('platform.operations.severityLabel')}</span>
                <Select
                  value={severity}
                  onChange={(value) => setSeverity(value as IncidentSeverity)}
                  options={INCIDENT_SEVERITIES.map((candidate) => ({
                    value: candidate,
                    label: t(`platform.operations.severity.${candidate}`),
                  }))}
                />
              </label>

              {dialog.kind === 'UPDATE' && (
                <label className={styles.field}>
                  <span className={styles.fieldLabel}>{t('platform.operations.statusLabel')}</span>
                  <Select
                    value={status}
                    onChange={(value) => setStatus(value as Exclude<IncidentStatus, 'RESOLVED'>)}
                    options={OPEN_INCIDENT_STATUSES.map((candidate) => ({
                      value: candidate,
                      label: t(`platform.operations.incidentStatus.${candidate}`),
                    }))}
                  />
                  <span className={styles.fieldHint}>{t('platform.operations.statusHint')}</span>
                </label>
              )}
            </div>

            <Checkbox
              label={t('platform.operations.platformWideLabel')}
              checked={platformWide}
              onChange={(event) => setPlatformWide(event.target.checked)}
            />

            {!platformWide && (
              <div className={styles.field}>
                <span className={styles.fieldLabel}>{t('platform.operations.institutionsLabel')}</span>
                {/* Selected institutions collect as removable chips; the state
                    stays the comma-joined list the submit path already sends. */}
                {institutionIds.trim() && (
                  <div className={styles.chipRow}>
                    {institutionIds
                      .split(',')
                      .map((id) => id.trim())
                      .filter(Boolean)
                      .map((id) => (
                        <span key={id} className={styles.chip}>
                          <InstitutionName institutionId={id} />
                          <button
                            type="button"
                            className={styles.chipRemove}
                            aria-label={t('common.remove')}
                            onClick={() =>
                              setInstitutionIds(
                                institutionIds
                                  .split(',')
                                  .map((entry) => entry.trim())
                                  .filter((entry) => entry && entry !== id)
                                  .join(', '),
                              )
                            }
                          >
                            ×
                          </button>
                        </span>
                      ))}
                  </div>
                )}
                <InstitutionPicker
                  id="incident-institutions"
                  value=""
                  placeholder={t('platform.operations.institutionsAdd')}
                  onChange={(next) => {
                    if (!next) return;
                    const current = institutionIds
                      .split(',')
                      .map((entry) => entry.trim())
                      .filter(Boolean);
                    if (!current.includes(next)) {
                      setInstitutionIds([...current, next].join(', '));
                    }
                  }}
                />
              </div>
            )}

            <label className={styles.field}>
              <span className={styles.fieldLabel}>{t('platform.operations.productsLabel')}</span>
              <input
                className={styles.input}
                value={productCodes}
                onChange={(event) => setProductCodes(event.target.value.toUpperCase())}
              />
              <span className={styles.fieldHint}>{t('platform.operations.productsHint')}</span>
            </label>
          </>
        )}

        {error && (
          <p className={styles.formError} role="alert">
            {error}
          </p>
        )}

        <div className="modal-actions">
          <button
            type="button"
            className="modal-button modal-button--secondary"
            onClick={onClose}
            disabled={submitting}
          >
            {t('common.cancel')}
          </button>
          <button type="submit" className="modal-button" disabled={!valid || submitting}>
            {submitting ? t('platform.confirm.submitting') : t('common.save')}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function ScheduleMaintenanceDialog({
  open,
  onClose,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [productCode, setProductCode] = useState('');
  const [institutionId, setInstitutionId] = useState('');
  const [mode, setMode] = useState<MaintenanceMode>('READ_ONLY');
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [userMessage, setUserMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setProductCode('');
    setInstitutionId('');
    setMode('READ_ONLY');
    setStartsAt('');
    setEndsAt('');
    setUserMessage('');
    setError(null);
  }, [open]);

  if (!open) return null;

  const chronological = Boolean(startsAt && endsAt && endsAt > startsAt);
  const valid =
    productCode.trim().length > 0 &&
    startsAt.length > 0 &&
    endsAt.length > 0 &&
    chronological &&
    userMessage.trim().length >= MIN_TEXT;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!valid || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await platformApi.operations.scheduleMaintenance({
        productCode: productCode.trim().toUpperCase(),
        institutionId: institutionId.trim() || undefined,
        mode,
        startsAt: new Date(startsAt).toISOString(),
        endsAt: new Date(endsAt).toISOString(),
        userMessage: userMessage.trim(),
      });
      onSaved();
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={t('platform.operations.scheduleTitle')}
      description={t('platform.operations.scheduleLead')}
      closeOnBackdropClick={false}
    >
      <form className={styles.dialogForm} onSubmit={submit} noValidate>
        <div className={styles.dialogRow}>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>{t('platform.operations.colProduct')}</span>
            <input
              className={styles.input}
              value={productCode}
              required
              autoComplete="off"
              onChange={(event) => setProductCode(event.target.value.toUpperCase())}
            />
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>{t('platform.operations.colMode')}</span>
            <Select
              value={mode}
              onChange={(value) => setMode(value as MaintenanceMode)}
              options={MAINTENANCE_MODES.map((candidate) => ({
                value: candidate,
                label: t(`platform.operations.mode.${candidate}`),
              }))}
            />
            <span className={styles.fieldHint}>{t('platform.operations.modeHint')}</span>
          </label>
        </div>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t('platform.operations.institutionOptional')}</span>
          <InstitutionPicker
            id="maintenance-institution"
            value={institutionId}
            clearable
            onChange={setInstitutionId}
          />
          <span className={styles.fieldHint}>{t('platform.operations.institutionOptionalHint')}</span>
        </label>

        <div className={styles.dialogRow}>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>{t('platform.operations.startsAt')}</span>
            <input
              className={styles.input}
              type="datetime-local"
              value={startsAt}
              required
              onChange={(event) => setStartsAt(event.target.value)}
            />
          </label>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>{t('platform.operations.endsAt')}</span>
            <input
              className={styles.input}
              type="datetime-local"
              value={endsAt}
              required
              min={startsAt || undefined}
              onChange={(event) => setEndsAt(event.target.value)}
            />
            {/*
              A window that ends before it starts is never in force, so the
              product stays up through work that assumed it was down.
            */}
            {startsAt && endsAt && !chronological && (
              <span className={styles.fieldError} role="alert">
                {t('platform.operations.windowOrderError')}
              </span>
            )}
          </label>
        </div>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t('platform.operations.colMessage')}</span>
          <textarea
            className={styles.textarea}
            rows={3}
            value={userMessage}
            required
            aria-invalid={userMessage.length > 0 && userMessage.trim().length < MIN_TEXT}
            onChange={(event) => setUserMessage(event.target.value)}
          />
          <span className={styles.fieldHint}>{t('platform.operations.messageHint')}</span>
        </label>

        {error && (
          <p className={styles.formError} role="alert">
            {error}
          </p>
        )}

        <div className="modal-actions">
          <button
            type="button"
            className="modal-button modal-button--secondary"
            onClick={onClose}
            disabled={submitting}
          >
            {t('common.cancel')}
          </button>
          <button type="submit" className="modal-button" disabled={!valid || submitting}>
            {submitting ? t('platform.confirm.submitting') : t('platform.operations.schedule')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
